"use client";

import React, { createContext, useContext } from "react";

/**
 * Who the visitor is, as far as the nav's right-hand links care — WAT-12640.
 *
 * ⚠ Decided in the browser, never on the server: the page HTML is edge-cached,
 * so a server-side pick would serve one visitor's signed-in links to the next.
 * The app supplies the value from its own cookie rule; the nav only reads it.
 * No provider means signed out, which is what the served HTML shows.
 */
export type NavAudienceValue = "auth" | "recog" | null;

export const NavAudienceContext = createContext<NavAudienceValue>(null);

export interface NavAudienceProps {
  /** Signed out — the server-rendered default. */
  anon: React.ReactNode;
  /** Recognized. Absent, a recognized visitor keeps `anon`. */
  recog?: React.ReactNode;
  /** Signed in. Absent, a signed-in visitor keeps `anon`. */
  auth?: React.ReactNode;
}

/** Picks one of three server-rendered copies of the same region. */
export function NavAudience({ anon, recog, auth }: NavAudienceProps) {
  const audience = useContext(NavAudienceContext);

  if (audience === "auth" && auth != null) {
    return <>{auth}</>;
  }
  if (audience === "recog" && recog != null) {
    return <>{recog}</>;
  }
  return <>{anon}</>;
}

/** Hides its children for any recognized visitor, signed in or recognized — the top bar and drawer phone, as light mode does. */
export function NavHideWhenRecognized({ children }: { children: React.ReactNode }) {
  const audience = useContext(NavAudienceContext);

  return audience === null ? <>{children}</> : null;
}
